/**
 * PostHog client for feature flags and analytics
 *
 * PostHog is the sole feature flag provider. This module handles:
 * - Feature flags (always enabled)
 * - Analytics (in staging/production environments)
 *
 * @example
 * ```typescript
 * import { initializePostHog, getFlag, identifyUser } from '@client/utils/feature-flags';
 *
 * // Initialize once at app startup
 * await initializePostHog();
 *
 * // Then use feature flags
 * const enabled = await getFlag('myFeature', false);
 * ```
 */

import { getConfig } from '@client/config/runtime';
import { useDebugStore } from '@client/stores/DebugStore';
import { scheduleOnIdle } from '@client/utils/scheduling/scheduleOnIdle';
import { redactAccessKeysDeep } from '@client/utils/telemetry/access-key-redaction';
import { isSessionRecordingBlocked } from '@client/utils/telemetry/recording-privacy';
import * as Sentry from '@sentry/react';
import { POSTHOG_BOOTSTRAP_WINDOW_KEY, type PostHogBootstrapData } from '@shared/config';
import { hasPermission } from '@shared/permissions/roles';
import {
    AnalyticsExtensions,
    FeatureFlagsExtensions,
    SessionReplayExtensions,
} from 'posthog-js/dist/extension-bundles';
import posthog from 'posthog-js/dist/module.slim';
import type { UserContext } from './types';

/**
 * Determines whether PostHog logs should be displayed.
 * Logs show when BOTH:
 * - isPosthogLogsEnabled is true (checkbox in debug panel)
 * - Debug mode is active (enable-debug-info feature flag OR isForceDebugEnabledFlag)
 */
function shouldLogPosthog(): boolean {
    const { isForceDebugEnabledFlag, isPosthogLogsEnabled } = useDebugStore.getState();
    const isDebugInfoEnabled = getFlagSync('enable-debug-info', false);
    return isPosthogLogsEnabled && (isDebugInfoEnabled || isForceDebugEnabledFlag);
}

/**
 * Feature flag client interface for router context
 *
 * This provides a provider-agnostic way to evaluate feature flags
 * in TanStack Router's beforeLoad hooks.
 */
export interface FeatureFlagClient {
    /**
     * Evaluate a feature flag
     * @param flagKey - The key of the feature flag
     * @param defaultValue - The default value if the flag is not found
     * @returns Promise resolving to the flag value
     */
    variation<T>(flagKey: string, defaultValue: T): Promise<T>;
}

/**
 * Flag to track if PostHog has been initialized by us
 */
let isInitialized = false;

/**
 * Promise tracking initialization to prevent duplicate attempts
 */
let initializationPromise: Promise<boolean> | null = null;

/**
 * Generation used to invalidate identification work suspended on initialisation.
 * Logging out increments this before resetting PostHog, so an older identify
 * call cannot resume afterwards and restore the logged-out user's identity.
 */
let identityGeneration = 0;

/**
 * Interval ID for feature flag polling
 */
let pollingIntervalId: ReturnType<typeof setInterval> | null = null;

/**
 * Feature-flag readiness — lets route guards tell "flag is off" from "flags have not been
 * evaluated yet" (both read as `false`/`undefined` through the SDK).
 *
 * Why the SDK's own `hasLoadedFlags` is not enough (CUR-891): a server bootstrap is applied
 * through the same `receivedFeatureFlags` path as a network response, so it both REPLACES the
 * flags persisted from the previous session (our bootstrap carries only the maintenance
 * registry — one or two keys) and marks flags as "loaded". Until `initializePostHog()` has
 * refreshed them, every other flag reads as off and `hasLoadedFlags` says that is final.
 *
 * - `initialisation`: outcome of `initializePostHog()` — `ready` once the SDK holds flags it
 *   evaluated itself (network response, or the forced refresh after an anonymous bootstrap) or
 *   the 5 s wait gave up; `unavailable` when PostHog is not configured or init threw, i.e. no
 *   flags will ever arrive and defaults are the final answer.
 * - `identifiedUserId` / `identifiedOrgId`: the user AND organisation whose flags
 *   `identifyUser()` has finished reloading. PostHog evaluates against the person and the
 *   organisation group, and `User.id` is the same across a user's org memberships, so an org
 *   switch must not inherit the previous organisation's map. `null` until then and after
 *   `identifyAnonymous()`.
 */
export type FeatureFlagReadiness = {
    readonly initialisation: 'pending' | 'ready' | 'unavailable';
    readonly identifiedUserId: string | null;
    readonly identifiedOrgId: string | null;
};

const NO_IDENTITY = { identifiedUserId: null, identifiedOrgId: null } as const;

let featureFlagReadiness: FeatureFlagReadiness = { initialisation: 'pending', ...NO_IDENTITY };
const featureFlagReadinessListeners = new Set<() => void>();

function setFeatureFlagReadiness(patch: Partial<FeatureFlagReadiness>): void {
    const next: FeatureFlagReadiness = { ...featureFlagReadiness, ...patch };
    if (
        next.initialisation === featureFlagReadiness.initialisation &&
        next.identifiedUserId === featureFlagReadiness.identifiedUserId &&
        next.identifiedOrgId === featureFlagReadiness.identifiedOrgId
    ) {
        return;
    }
    featureFlagReadiness = next;
    for (const listener of featureFlagReadinessListeners) {
        listener();
    }
}

/** Current readiness snapshot — stable identity until it changes (safe for `useSyncExternalStore`). */
export function getFeatureFlagReadiness(): FeatureFlagReadiness {
    return featureFlagReadiness;
}

/** Subscribe to readiness changes; returns the unsubscribe function. */
export function subscribeFeatureFlagReadiness(listener: () => void): () => void {
    featureFlagReadinessListeners.add(listener);
    return () => {
        featureFlagReadinessListeners.delete(listener);
    };
}

/**
 * Get PostHog configuration from environment
 */
function getPostHogConfig() {
    return {
        apiKey: getConfig('PUBLIC_POSTHOG_KEY', import.meta.env.VITE_PUBLIC_POSTHOG_KEY),
        host: getConfig('PUBLIC_POSTHOG_HOST', import.meta.env.VITE_PUBLIC_POSTHOG_HOST) || 'https://eu.i.posthog.com',
        uiHost: getConfig('PUBLIC_POSTHOG_UI_HOST', import.meta.env.VITE_PUBLIC_POSTHOG_UI_HOST),
        env: getConfig('ENV', import.meta.env.VITE_ENV),
    };
}

/**
 * Stop session recording for the rest of this document. Used when in-app navigation lands on a
 * location that carries a secret; the next full page load re-evaluates from `initializePostHog`.
 * A no-op before the SDK has loaded — the deferred start re-checks the location itself.
 */
export function stopPostHogSessionRecording(): void {
    if (posthog.__loaded) {
        posthog.stopSessionRecording();
    }
}

/**
 * Check if analytics should be enabled (staging/production)
 */
function shouldEnableAnalytics(): boolean {
    const { env } = getPostHogConfig();
    return env === 'staging' || env === 'production';
}

/**
 * Get bootstrap data from server-injected window property.
 * This data is pre-fetched by the server and injected into HTML.
 */
function getBootstrapData(): PostHogBootstrapData {
    return (window as unknown as Record<string, PostHogBootstrapData>)[POSTHOG_BOOTSTRAP_WINDOW_KEY] ?? null;
}

/**
 * Read a flag value from server-injected bootstrap data.
 * Used as a fallback when PostHog has not finished initialising — keeps
 * feature-flag-gated routes correct on the very first render after a cold
 * load, where the React tree may mount before `initializePostHog()` resolves.
 *
 * Returns `undefined` when the bootstrap data is missing or does not contain
 * the requested key; callers should fall back to their own default in that case.
 */
function readBootstrapFlag<T>(flagKey: string): T | undefined {
    const bootstrap = getBootstrapData();
    const value = bootstrap?.featureFlags?.[flagKey];
    return value === undefined ? undefined : (value as T);
}

/**
 * Initialize PostHog
 *
 * Call this once at app startup. It will:
 * - Initialize PostHog with appropriate config
 * - Wait for feature flags to load (and refresh an anonymous server bootstrap)
 * - Enable analytics in staging/production
 *
 * @returns Promise resolving to true if initialized successfully
 */
export async function initializePostHog(): Promise<boolean> {
    // Already initialized
    if (isInitialized) {
        return true;
    }

    // Already initializing
    if (initializationPromise) {
        return initializationPromise;
    }

    initializationPromise = (async () => {
        const config = getPostHogConfig();
        const enableAnalytics = shouldEnableAnalytics();

        // A retry after a failed attempt starts unknown again — `unavailable` must not
        // outlive the attempt that produced it (no-op on the first attempt).
        setFeatureFlagReadiness({ initialisation: 'pending' });

        if (!config.apiKey) {
            console.warn('[PostHog] API key not configured, skipping initialization');
            setFeatureFlagReadiness({ initialisation: 'unavailable' });
            return false;
        }

        // Already loaded by something else
        if (posthog.__loaded) {
            if (shouldLogPosthog()) {
                console.log('[PostHog] Already initialized, reusing instance');
            }
            isInitialized = true;
            setFeatureFlagReadiness({ initialisation: 'ready' });
            return true;
        }

        // Get server-injected bootstrap data (pre-fetched feature flags)
        const bootstrapData = getBootstrapData();
        const hasBootstrap = bootstrapData !== null && Object.keys(bootstrapData.featureFlags).length > 0;

        if (shouldLogPosthog()) {
            console.log('[PostHog] Initializing...', {
                enableAnalytics,
                hasBootstrap,
                bootstrapFlagCount: bootstrapData?.featureFlags ? Object.keys(bootstrapData.featureFlags).length : 0,
            });
        }

        try {
            // Initialize PostHog with optional bootstrap data.
            //
            // Bundle strategy: we import from `posthog-js/dist/module.slim`
            // and inject only the extensions we actually use via
            // `__extensionClasses`. This stops the SDK from fetching its
            // extension scripts (recorder, web-vitals, dead-clicks,
            // autocapture, surveys, …) from PostHog's CDN at runtime —
            // staging Sentry traces showed those four cold-cache fetches
            // costing ~2 s each on the critical path. The extension code
            // now ships in our `analytics-posthog` chunk, served from
            // Cloudflare with `Cache-Control: immutable` (paid once per
            // release, not every cold session). PostHog's official
            // recommendation for PageSpeed-sensitive apps is this exact
            // pattern — see https://posthog.com/docs/libraries/js
            // ("JavaScript Web Library — slim bundle").
            //
            // Session-recording deferral: even with the recorder code
            // bundled, the recorder's runtime buffer still competes for
            // main-thread time. We start with `disable_session_recording:
            // true` and call `posthog.startSessionRecording()` from
            // `requestIdleCallback` post-LCP (below). This uses PostHog's
            // documented `startSessionRecording` API rather than the
            // lazy-load pattern PostHog warns against.
            posthog.init(config.apiKey, {
                api_host: config.host,
                // When using a reverse proxy, ui_host ensures PostHog toolbar/session replay links
                // point back to the PostHog dashboard rather than the proxy domain
                ui_host: config.uiHost || undefined,
                person_profiles: 'identified_only',
                // Analytics: enabled in staging/prod
                autocapture: enableAnalytics,
                capture_pageview: enableAnalytics,
                capture_pageleave: enableAnalytics,
                // Public access-key URLs are bearer credentials (driver status link, invoice
                // view, invite secret). Strip them from every property before it leaves the
                // browser — `$current_url`, `$pathname`, `$referrer` and anything nested.
                before_send: (event) => {
                    if (event === null) return null;
                    if (event.event === '$snapshot') return isSessionRecordingBlocked() ? null : event;
                    return redactAccessKeysDeep(event);
                },
                // Always start with recording disabled; we re-enable below
                // via `startSessionRecording()` once the page has settled.
                disable_session_recording: true,
                // Bootstrap: pre-load feature flags from server
                bootstrap: bootstrapData
                    ? {
                          distinctID: bootstrapData.distinctId,
                          isIdentifiedID: bootstrapData.isIdentifiedID,
                          featureFlags: bootstrapData.featureFlags,
                      }
                    : undefined,
                // Slim-bundle extension injection. Order doesn't matter;
                // each bundle merges its extension classes into the SDK.
                //
                // The `as unknown as never` cast works around a typings
                // quirk in posthog-js@1.372.10 where the extension-bundles
                // `.d.ts` imports `ExtensionConstructor<X>` from `./module.slim`
                // while `posthog.init`'s typing of `__extensionClasses`
                // resolves through `./module`. The two declarations are
                // structurally identical at runtime — TypeScript only
                // refuses the assignment because the classes carry a
                // private `_instance` field tied to the module path.
                // Tracked upstream at https://github.com/PostHog/posthog-js;
                // remove the cast when the slim-bundle types align.
                __extensionClasses: {
                    ...FeatureFlagsExtensions,
                    ...AnalyticsExtensions,
                    ...SessionReplayExtensions,
                } as unknown as never,
                loaded: () => {
                    if (shouldLogPosthog()) {
                        console.log('[PostHog] Client loaded', { hasBootstrap });
                    }
                },
            });

            // Re-enable session recording after the page has had a chance
            // to paint LCP and dispatch its critical work. Uses
            // `scheduleOnIdle` so Safari (no `requestIdleCallback`) still
            // gets a proper post-load grace via the shared fallback.
            // Access-key pages (driver status link, lodgement summary, invoice view) and the
            // invite link are addressed by a bearer credential in the URL and are read-only
            // public pages: never record them. The location is checked again inside the idle
            // callback — an in-app navigation onto a secret location between init and idle
            // (see `trackSecretLocationRecordingGate`) must not be followed by a late start.
            if (enableAnalytics && !isSessionRecordingBlocked()) {
                scheduleOnIdle(() => {
                    if (!isSessionRecordingBlocked()) {
                        posthog.startSessionRecording();
                    }
                });
            }

            // An anonymous bootstrap is only a synchronous first-paint hint. It
            // must not make route guards observe "ready" until the SDK has
            // refreshed flags for its persisted distinct ID. Subscribe before
            // triggering the refresh so even a synchronous callback is caught.
            let outcome: FlagWaitOutcome = 'delivered';
            if (hasBootstrap && bootstrapData?.isIdentifiedID !== true) {
                outcome = await waitForFeatureFlags(5000, { reload: true });
            } else if (!hasBootstrap) {
                outcome = await waitForFeatureFlags(5000);
            }

            isInitialized = true;
            // A delivery, or an exhausted budget, makes the current values final. A failed
            // delivery leaves readiness pending: nothing was evaluated, and a later attempt
            // (the identify reload) may still deliver — consumers bound their own wait.
            if (outcome !== 'failed') {
                setFeatureFlagReadiness({ initialisation: 'ready' });
            }

            Sentry.addBreadcrumb({
                category: 'posthog',
                message: `PostHog initialized (analytics: ${enableAnalytics})`,
                level: 'info',
            });

            return true;
        } catch (error) {
            console.error('[PostHog] Initialization failed:', error);
            Sentry.captureException(error, {
                tags: { location: 'posthogClient', operation: 'initialize' },
                level: 'warning',
            });
            // No flags will arrive from this attempt — consumers must act on their defaults
            // rather than wait. A later successful retry flips this back to `ready`.
            setFeatureFlagReadiness({ initialisation: 'unavailable' });
            initializationPromise = null;
            return false;
        }
    })();

    return initializationPromise;
}

/**
 * Wait for feature flags to load with timeout
 *
 * Uses a resolved flag to handle the case where onFeatureFlags fires synchronously
 * (which can happen if flags are already cached).
 */
/**
 * How a wait for flags ended. `failed` = the SDK delivered a failed `/flags` request
 * (`errorsLoading`): nothing was evaluated, but nothing more will arrive from this attempt
 * either, so callers must NOT keep waiting — only readiness treats it differently from
 * `delivered`.
 */
type FlagWaitOutcome = 'delivered' | 'failed' | 'timeout';

function waitForFeatureFlags(timeoutMs: number, options?: { reload?: boolean }): Promise<FlagWaitOutcome> {
    return new Promise((resolve) => {
        let resolved = false;
        let reloadStarted = options?.reload !== true;
        const cleanup = { unsubscribe: () => {} };

        const timeoutId = setTimeout(() => {
            if (resolved) return;
            resolved = true;
            cleanup.unsubscribe();
            if (shouldLogPosthog()) {
                console.warn('[PostHog] Feature flags loading timed out');
            }
            resolve('timeout');
        }, timeoutMs);

        cleanup.unsubscribe = posthog.onFeatureFlags((_flags, _variants, context) => {
            // Registering after bootstrap can synchronously replay the
            // anonymous values. Ignore that replay and resolve only from the
            // refresh triggered below.
            if (resolved || !reloadStarted) return;
            resolved = true;
            clearTimeout(timeoutId);
            cleanup.unsubscribe();
            // A failed `/flags` request (non-200, timeout, network — e.g. a tracker blocker)
            // also fires this callback, with `errorsLoading` set. Resolve immediately so
            // every awaiter of `initializePostHog()` (embedded entry, beforeLoad gates)
            // keeps its pre-existing latency; the outcome tells readiness that nothing was
            // evaluated.
            const outcome: FlagWaitOutcome = context?.errorsLoading ? 'failed' : 'delivered';
            if (shouldLogPosthog()) {
                console.log(`[PostHog] Feature flags ${outcome}`);
            }
            resolve(outcome);
        });

        if (options?.reload) {
            reloadStarted = true;
            posthog.reloadFeatureFlags();
        }
    });
}

/**
 * Check if PostHog is ready
 */
export function isPostHogReady(): boolean {
    return isInitialized && (posthog.__loaded ?? false);
}

/**
 * Get a feature flag value asynchronously
 */
export async function getFlag<T>(flagKey: string, defaultValue: T): Promise<T> {
    if (!isPostHogReady()) {
        // Fall back to server-injected bootstrap so callers running during the
        // brief window before init resolves still get the correct value.
        const bootstrapValue = readBootstrapFlag<T>(flagKey);
        if (bootstrapValue !== undefined) {
            return bootstrapValue;
        }
        if (shouldLogPosthog()) {
            console.warn(`[PostHog] Not ready, returning default for flag: ${flagKey}`);
        }
        return defaultValue;
    }

    try {
        const value = posthog.getFeatureFlag(flagKey);

        if (shouldLogPosthog()) {
            console.log(`[PostHog] Flag: ${flagKey} =`, value ?? defaultValue);
        }

        return (value ?? defaultValue) as T;
    } catch (error) {
        console.error(`[PostHog] Error getting flag ${flagKey}:`, error);
        return defaultValue;
    }
}

/**
 * Get a feature flag value synchronously
 */
export function getFlagSync<T>(flagKey: string, defaultValue: T): T {
    if (!isPostHogReady()) {
        const bootstrapValue = readBootstrapFlag<T>(flagKey);
        return bootstrapValue !== undefined ? bootstrapValue : defaultValue;
    }

    try {
        const value = posthog.getFeatureFlag(flagKey);
        return (value ?? defaultValue) as T;
    } catch {
        return defaultValue;
    }
}

/**
 * Get all feature flags
 */
export function getAllFlags(): Record<string, boolean | string | undefined> {
    if (!isPostHogReady()) {
        return {};
    }

    try {
        // `featureFlags` is optional on the slim PostHog type — guard so the
        // accessor doesn't throw if the FeatureFlagsExtensions bundle is not
        // injected (defence-in-depth; we do inject it in `init()`).
        return posthog.featureFlags?.getFlagVariants() ?? {};
    } catch {
        return {};
    }
}

/**
 * Person properties set on identify.
 *
 * `isOrgAdmin` is the flag-targeting property, mirrored exactly by the server
 * (`buildPostHogPersonProperties`). Flag conditions must target these
 * exact-match tier properties, never `roles`: the client sends `roles` as an
 * array while the server passes a comma-joined string, so a `roles` condition
 * evaluates differently per side. `roles` stays for analytics only.
 */
function buildIdentifyProperties(userContext: UserContext) {
    return {
        email: userContext.email,
        name: userContext.name,
        orgId: userContext.orgId,
        orgName: userContext.orgName,
        roles: userContext.roles,
        isOrgAdmin: hasPermission(userContext.roles, 'org:admin') ? 'true' : 'false',
    };
}

/**
 * Internal sync function to identify a user with PostHog
 */
function identifyUserInternal(userContext: UserContext): boolean {
    if (!isPostHogReady()) {
        console.warn('[PostHog] Not ready, cannot identify user');
        return false;
    }

    try {
        const currentDistinctId = posthog.get_distinct_id?.();

        if (shouldLogPosthog()) {
            console.log('[PostHog] Identifying user', userContext);
            console.log('[PostHog] Current distinct ID', currentDistinctId);
        }
        // Already identified as this user - just update properties
        if (currentDistinctId === userContext.userId) {
            if (shouldLogPosthog()) {
                console.log('[PostHog] User already identified, updating properties');
            }
            posthog.setPersonProperties(buildIdentifyProperties(userContext));
        } else {
            // First time identify
            posthog.identify(userContext.userId, buildIdentifyProperties(userContext));

            if (shouldLogPosthog()) {
                console.log('[PostHog] User identified:', userContext.userId);
            }
        }

        // Set organization group
        posthog.group('organization', userContext.orgId, {
            name: userContext.orgName,
        });

        Sentry.addBreadcrumb({
            category: 'posthog',
            message: 'User identified',
            level: 'info',
            data: { userId: userContext.userId, orgId: userContext.orgId },
        });
        return true;
    } catch (error) {
        console.error('[PostHog] Error identifying user:', error);
        Sentry.captureException(error, {
            tags: { location: 'posthogClient', operation: 'identifyUser' },
        });
        return false;
    }
}

/**
 * Identify a user with PostHog and reload feature flags
 *
 * Call this after user login to ensure flags are evaluated
 * with the correct user context.
 *
 * Awaits initialisation first: on a cold load without server bootstrap data,
 * `initializePostHog()` stays pending for up to 5 s while flags load, and the
 * user/org queries can resolve before it does. Without this await both
 * `identifyUserInternal` and `reloadFeatureFlags` would hit their not-ready
 * guards and silently no-op — leaving the session evaluating flags as
 * anonymous until the next full reload (e.g. `fp-demo`-gated nav items
 * missing for the whole session).
 *
 * @param userContext - User information
 * @returns Whether the user remains identified after feature flags reload
 */
export async function identifyUser(userContext: UserContext): Promise<boolean> {
    const generationAtStart = identityGeneration;
    // Whatever the SDK holds now was evaluated for the previous identity / organisation (or
    // the persisted distinct ID) — targeted flags are unknown for this context until the
    // reload lands.
    setFeatureFlagReadiness(NO_IDENTITY);
    const initialized = await initializePostHog();
    if (!initialized || generationAtStart !== identityGeneration) {
        // Not configured (no API key) or init failed — nothing to identify against.
        // A generation change means logout reset the identity while init was pending.
        return false;
    }
    const identified = identifyUserInternal(userContext);
    const reloaded = identified && (await reloadFeatureFlags());
    const isCurrentIdentity = generationAtStart === identityGeneration;
    if (reloaded && isCurrentIdentity) {
        // Only a delivered reload counts. A failed identify is NOT "nothing more is coming":
        // `posthog.identify()` may already have succeeded and scheduled the SDK's own reload
        // before a later statement threw; and a reload that timed out or failed evaluated
        // nothing. Publishing in either case would let a guard judge the previous map as
        // final — leave it unsettled and let `useAreFeatureFlagsSettled`'s timeout bound
        // the wait. A delivered reload is an evaluation in its own right, so it also settles
        // an initialisation whose own delivery failed.
        setFeatureFlagReadiness({
            initialisation: 'ready',
            identifiedUserId: userContext.userId,
            identifiedOrgId: userContext.orgId,
        });
    }
    return identified && isCurrentIdentity;
}

/**
 * Reset to anonymous user
 */
export function identifyAnonymous(): void {
    // Invalidate suspended identify calls before resetting the SDK. During cold
    // initialisation `posthog.__loaded` is already true while `isInitialized`
    // remains false until feature flags arrive, so use the SDK readiness marker
    // here rather than isPostHogReady().
    identityGeneration += 1;
    setFeatureFlagReadiness(NO_IDENTITY);

    if (!posthog.__loaded) {
        return;
    }

    try {
        posthog.reset();
        if (shouldLogPosthog()) {
            console.log('[PostHog] Reset to anonymous');
        }
    } catch (error) {
        console.error('[PostHog] Error resetting:', error);
    }
}

/**
 * Reload feature flags from server.
 *
 * @returns `true` only when the SDK delivered a successful response for this reload;
 * `false` when PostHog is not ready, the reload failed, or the 3 s budget ran out.
 */
export async function reloadFeatureFlags(): Promise<boolean> {
    if (!isPostHogReady()) {
        return false;
    }

    return new Promise((resolve) => {
        let resolved = false;
        let reloadStarted = false;

        const cleanup = { unsubscribe: () => {} };

        const timeoutId = setTimeout(() => {
            if (resolved) return;
            resolved = true;
            cleanup.unsubscribe();
            resolve(false);
        }, 3000);

        cleanup.unsubscribe = posthog.onFeatureFlags((_flags, _variants, context) => {
            // posthog-js synchronously replays the CURRENT map to a new listener when
            // flags are already loaded. That is the previous identity's map, not the
            // reload — resolving on it would publish "flags reloaded for this user"
            // before the request for this user has even been sent (and would leak the
            // handler, since the unsubscribe function is not assigned yet).
            if (resolved || !reloadStarted) return;
            resolved = true;
            clearTimeout(timeoutId);
            cleanup.unsubscribe();
            // A failed reload fires the callback with `errorsLoading`; nothing was
            // evaluated, and nothing more will arrive from this reload.
            const delivered = context?.errorsLoading !== true;
            if (shouldLogPosthog()) {
                console.log(delivered ? '[PostHog] Feature flags reloaded' : '[PostHog] Feature flags reload failed');
            }
            resolve(delivered);
        });

        reloadStarted = true;
        posthog.reloadFeatureFlags();
    });
}

/**
 * Get a feature flag client for use in router context
 *
 * This returns a client that can be used in TanStack Router's beforeLoad hooks.
 *
 * @returns A feature flag client with a variation method
 */
export function getFeatureFlagClient(): FeatureFlagClient {
    return {
        async variation<T>(flagKey: string, defaultValue: T): Promise<T> {
            return getFlag(flagKey, defaultValue);
        },
    };
}

/**
 * Start polling for feature flag updates
 *
 * PostHog does not natively support real-time feature flag updates.
 * This function starts periodic polling to refresh flags at a configurable interval.
 *
 * @param intervalMs - Polling interval in milliseconds (default: 30000 = 30s)
 * @returns Cleanup function to stop polling
 *
 * @example
 * ```typescript
 * // Start polling every 30 seconds
 * const stopPolling = startFeatureFlagPolling(30000);
 *
 * // Later, stop polling
 * stopPolling();
 * ```
 */
export function startFeatureFlagPolling(intervalMs: number = 30000): () => void {
    // Clear any existing polling
    if (pollingIntervalId) {
        clearInterval(pollingIntervalId);
    }

    if (shouldLogPosthog()) {
        console.log(`[PostHog] Starting feature flag polling every ${intervalMs}ms`);
    }

    pollingIntervalId = setInterval(() => {
        if (isPostHogReady()) {
            posthog.reloadFeatureFlags();
            if (shouldLogPosthog()) {
                console.log('[PostHog] Feature flags refreshed via polling');
            }
        }
    }, intervalMs);

    return () => {
        stopFeatureFlagPolling();
    };
}

/**
 * Stop feature flag polling
 */
export function stopFeatureFlagPolling(): void {
    if (pollingIntervalId) {
        clearInterval(pollingIntervalId);
        pollingIntervalId = null;
        if (shouldLogPosthog()) {
            console.log('[PostHog] Stopped feature flag polling');
        }
    }
}
